import BaseImage from "@components/BaseImage";
import {
  BW_VARIANT,
  TBW_VARIANT,
  TChainSelection,
} from "@components/BridgeWrapV2/types";
import BreadCrum from "@components/BridgeWrapV2/BreadCrum";
import LearnMoreV2 from "@components/BridgeWrapV2/LearnMoreAccordianV2";
import BridgeJourneyStepsV2 from "@components/BridgeWrapV2/StepJourney2";
import AgEthJourneyStepsV2 from "@components/BridgeWrapV2/StepsJourneyAgethV2";
import {
  CONNECT_WALLET,
  BRIDGE_CHAIN_SELECTED,
  LEARN_MORE_CLICK,
} from "@constants/analytics";
import { WrapBridgeMappings } from "@constants/common";
import useBridgeWrapConfig from "@hooks/useBridgeWrapConfig";
import useSanction from "@hooks/useSanction";
import {
  Button,
  Card,
  InputDropdown,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { WarningIcon } from "@phosphor-icons/react";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import React, { useRef, useState } from "react";
import { trackEvent } from "utils";
import { useAccount } from "wagmi";

type BridgeWrapProps = {
  variant: TBW_VARIANT;
};

const BridgeWrapV2: React.FC<BridgeWrapProps> = ({ variant }) => {
  const agETHJourney = variant === BW_VARIANT.AGETH;

  const { isConnected, chain } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { isSanctioned } = useSanction();

  const BRIDGE_WRAP_DATA = useBridgeWrapConfig(variant);

  const [sourceChain, setSourceChain] = useState<TChainSelection>();

  const [destinationChain, setDestinationChain] = useState<TChainSelection>();

  const accordionRef = useRef<{ openAccordion: () => void }>(null);

  const goToLearnMore = () => {
    trackEvent(LEARN_MORE_CLICK, {
      dapp: agETHJourney ? "vault" : "kelp",
      activity: "bridge",
    });
    accordionRef.current?.openAccordion();
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "bridge_wrap_page",
      dapp: agETHJourney ? "vault" : "kelp",
    });
  };

  const handleOnChainSelection = (option: any) => {
    console.log("option", option);
    const selectedChain = {
      id: Number(option?.id),
      title: option?.label || "",
    };
    setSourceChain(selectedChain);

    // Track source chain selection
    if (destinationChain) {
      trackEvent(BRIDGE_CHAIN_SELECTED, {
        source_chain: sourceChain?.title || "",
        destination_chain: destinationChain?.title || "",
        from_chain_id: String(sourceChain?.id),
        to_chain_id: String(destinationChain?.id),
        dapp: agETHJourney ? "vault" : "kelp",
      });
    }
  };

  const handleDestinationChainSelection = (option: any) => {
    console.log("option", option);
    const selectedChain = {
      id: Number(option?.id),
      title: option?.label || "",
    };
    setDestinationChain(selectedChain);

    // Track destination chain selection
    if (sourceChain) {
      trackEvent(BRIDGE_CHAIN_SELECTED, {
        source_chain: sourceChain?.title || "",
        destination_chain: selectedChain?.title || "",
        from_chain_id: String(sourceChain?.id),
        to_chain_id: String(selectedChain?.id),
        dapp: agETHJourney ? "vault" : "kelp",
      });
    }
  };

  // Define chains where assets are natively available (no bridging/wrapping needed)
  const getNativeSupportedChains = () => {
    if (agETHJourney) {
      return [1]; // Only Ethereum for agETH
    }
    return [1, 42161, 130]; // Ethereum, Arbitrum, Unichain for rsETH
  };

  const isNativelySupported = (chainId: number) => {
    const nativeChains = getNativeSupportedChains();
    return nativeChains.includes(chainId);
  };

  const shouldShowNoWrapMessage = () => {
    return (
      sourceChain?.id &&
      destinationChain?.id &&
      isNativelySupported(sourceChain.id) &&
      sourceChain.id === destinationChain.id
    );
  };

  const renderMessage = () => {
    if (shouldShowNoWrapMessage()) {
      return (
        <div className="text-text-primary flex items-start gap-1">
          <div className="flex items-center justify-center h-max w-max mt-0.5">
            <WarningIcon
              color="#FF6B6B"
              weight="regular"
              className="object-contain h-3 w-auto"
            />
          </div>
          {/* <p className="text-xs font-normal">
            You do not need bridging or wrapping. Start exploring DeFi.
          </p> */}
          <Typography
            variant="xsmall"
            type="body"
            className="text-text-primary"
          >
            You do not need bridging or wrapping. Start exploring DeFi.
          </Typography>
        </div>
      );
    }
  };
  const renderSteps = () => {
    const isChainsSelected = sourceChain?.id && destinationChain?.id;
    if (!isConnected && !chain) {
      return (
        <div className="flex flex-col gap-4 w-full ">
          <div className="flex h-8 justify-between items-center w-full  py-6 px-0 md:px-8">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              Connect your wallet to fetch tokens & view steps.
            </Typography>
            <Button
              size="xsmall"
              variant="primary"
              onClick={handleConnectWallet}
            >
              Connect Wallet
            </Button>
          </div>
          {/* <hr className="bg-[#B3CECE] h-[1px]" /> */}
        </div>
      );
    }

    if (!isChainsSelected) {
      return (
        <div className="flex flex-col gap-4 w-full ">
          <Typography
            variant="xsmall"
            type="body"
            className="text-text-secondary px-0 md:px-8"
          >
            Select a source and destination to view steps
          </Typography>
          {/* <hr className="bg-[#B3CECE] h-[1px]" /> */}
        </div>
      );
    }

    if (sourceChain.id === -1) {
      return (
        <p className="text-xs font-normal">
          Please selected a supported source chain
        </p>
      );
    }

    return (
      sourceChain.id &&
      destinationChain.id &&
      (agETHJourney ? (
        <AgEthJourneyStepsV2
          sourceChain={sourceChain}
          destinationChain={destinationChain}
        />
      ) : (
        <BridgeJourneyStepsV2
          sourceChain={sourceChain}
          destinationChain={destinationChain}
        />
      ))
    );
  };

  return (
    <>
      <div
        className={`flex justify-center mx-auto w-full xl:w-[548px] lg:w-[568px] md:w-[592px] sm:w-[375px] ${
          isSanctioned
            ? "opacity-50 pointer-events-none"
            : "opacity-100 pointer-events-auto"
        }`}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <BreadCrum variant={variant} className="mb-0 md:mb-2" />
            <Typography
              as="h1"
              type="heading"
              className={`w-full text-center md:text-left font-sans font-medium lg:font-semibold`}
            >
              Bridge {variant === BW_VARIANT.AGETH ? "& Wrap agETH" : "rsETH"}
            </Typography>
            <Typography
              variant="xsmall"
              type="body"
              className={`text-text-secondary w-full text-center md:text-left`}
            >
              Move your tokens across chains, without losing usability
            </Typography>
          </div>
          <Card
            variant="non-bordered"
            className="w-full p-4 md:p-8 rounded-2xl"
          >
            <div className="flex flex-col gap-5">
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <InputDropdown
                  label="From"
                  width="full"
                  options={BRIDGE_WRAP_DATA.sourceChains.map((chain) => ({
                    id: chain.id.toString(),
                    label: chain.title,
                    iconSrc:
                      WrapBridgeMappings.ICONS[chain.id] ||
                      WrapBridgeMappings.ICONS.placeholder,
                  }))}
                  selectedOption={
                    sourceChain
                      ? {
                          id: sourceChain.id.toString(),
                          label: sourceChain.title,
                          iconSrc:
                            WrapBridgeMappings.ICONS[sourceChain.id] ||
                            WrapBridgeMappings.ICONS.placeholder,
                        }
                      : undefined
                  }
                  onOptionSelect={handleOnChainSelection}
                  placeholder="Select chain"
                  className="bg-bg-primary"
                  ImageComponent={BaseImage}
                />
                <InputDropdown
                  label="To"
                  width="full"
                  options={BRIDGE_WRAP_DATA.destinationChains.map((chain) => ({
                    id: chain.id.toString(),
                    label: chain.title,
                    iconSrc:
                      WrapBridgeMappings.ICONS[chain.id] ||
                      WrapBridgeMappings.ICONS.placeholder,
                  }))}
                  selectedOption={
                    destinationChain
                      ? {
                          id: destinationChain.id.toString(),
                          label: destinationChain.title,
                          iconSrc:
                            WrapBridgeMappings.ICONS[destinationChain.id] ||
                            WrapBridgeMappings.ICONS.placeholder,
                        }
                      : undefined
                  }
                  onOptionSelect={handleDestinationChainSelection}
                  placeholder="Select chain"
                  className="bg-bg-primary"
                  ImageComponent={BaseImage}
                />
              </div>
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                Keep source & destination chain the same if you only want to
                wrap an asset.{" "}
                <span
                  onClick={goToLearnMore}
                  className="underline cursor-pointer hover:text-text-primary transition-colors"
                >
                  Learn more
                </span>{" "}
                about wrapped assets.
              </Typography>

              {renderMessage()}
            </div>
          </Card>
          {/* <hr className="bg-[#B3CECE] h-[1px]" /> */}

          <div className="flex">{renderSteps()}</div>
          <div className="flex flex-col gap-4 md:gap-6 w-full">
            <LearnMoreV2 variant={variant} ref={accordionRef} />
          </div>
        </div>
      </div>
    </>
  );
};

export default BridgeWrapV2;
